import useEditorProps from '@hooks/useEditorProps';
import useEmailEditor from '@hooks/useEmailEditor';
import useProperty from '@hooks/useProperty';
import {
    DeleteOutlineRounded,
    DriveFileRenameOutlineRounded,
    MoreVertRounded,
} from '@mui/icons-material';
import {
    Box,
    ButtonBase,
    IconButton,
    InputBase,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function CustomBlock({ item }) {
    const { id, name, thumbnail, block } = item;
    const { t } = useTranslation();
    const { setDraggedElement } = useEmailEditor();
    const { updateBlock, deleteBlock } = useEditorProps();
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [rename, setRename] = useState(false);
    const [newName, setNewName] = useState(name);

    const { getProperty: emailBgColor } = useProperty(
        'style.backgroundColor',
        'attributes',
        [0, 0],
    );
    const { getProperty: contentBgColor } = useProperty(
        'style.backgroundColor',
        'attributes',
        [0, 0, 0],
    );

    const inputRef = useRef(null);

    const onDragStart = (event: React.DragEvent, type: String) => {
        setDraggedElement(type);
        event.dataTransfer.setData(
            'add-saved-block/' + type,
            JSON.stringify(block),
        );
    };

    return (
        <Box
            position="relative"
            sx={{
                ...(!menuAnchorEl
                    ? {
                          '& #menu-button': {
                              display: 'none',
                          },
                      }
                    : {}),
                '&:hover #menu-button': {
                    display: 'block',
                },
            }}
        >
            <Box
                {...(!rename
                    ? {
                          draggable: true,
                          onDragStart: e => onDragStart(e, block.type),
                          onDragEnd: () => setDraggedElement(null),
                          component: ButtonBase,
                      }
                    : {})}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: 84,
                    height: 84,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    transform: 'translate(0, 0)',
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                    cursor: 'grab',
                    boxShadow: theme =>
                        `0px 0px 5px 0px ${theme.palette.divider}`,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: 60,
                        overflow: 'hidden',
                    }}
                >
                    <img
                        src={thumbnail}
                        alt={name}
                        // width="100%"
                        // height="100%"
                        draggable={false}
                        style={{
                            maxWidth: '100%',
                            minHeight: '100%',
                            objectFit: 'cover',
                            backgroundColor: contentBgColor() || emailBgColor(),
                        }}
                    />
                </Box>
                <Typography
                    display="inline-block"
                    variant="caption"
                    marginY="auto"
                    paddingX={!rename ? 0.5 : 'none'}
                    width="100%"
                    noWrap
                >
                    {rename ? (
                        <InputBase
                            defaultValue={name}
                            inputRef={inputRef}
                            sx={{
                                all: 'inherit',
                                pointerEvents: 'auto',
                                paddingX: 0,
                                overflow: 'hidden',
                                '& input': {
                                    borderRadius: 1,
                                    maxWidth: 76,
                                    paddingY: 0,
                                    paddingX: 0.25,
                                    border: theme =>
                                        `1px solid ${theme.palette.primary.main}`,
                                },
                                // outlineOffset: -4,
                            }}
                            onChange={e => setNewName(e.target.value)}
                            onBlur={() => {
                                updateBlock({ id, name: newName });
                                setRename(false);
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    updateBlock({ id, name: newName });
                                    setRename(false);
                                }
                            }}
                            autoFocus
                        />
                    ) : (
                        name
                    )}
                </Typography>
                <Menu
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={() => setMenuAnchorEl(null)}
                    MenuListProps={{
                        dense: true,
                        sx: { paddingY: 0.5 },
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                            },
                        },
                    }}
                >
                    <MenuItem
                        sx={{
                            justifyContent: 'space-between',
                            color: theme => theme.palette.error.main,
                        }}
                        onClick={() => {
                            deleteBlock(id);
                            setMenuAnchorEl(null);
                        }}
                    >
                        <Typography variant="inherit" marginRight={1}>
                            {t('Remove')}
                        </Typography>
                        <DeleteOutlineRounded fontSize="small" />
                    </MenuItem>
                    <MenuItem
                        sx={{ justifyContent: 'space-between' }}
                        onClick={() => {
                            setRename(true);
                            setMenuAnchorEl(null);
                        }}
                    >
                        <Typography variant="inherit" marginRight={1}>
                            {t('Rename')}
                        </Typography>
                        <DriveFileRenameOutlineRounded fontSize="small" />
                    </MenuItem>
                </Menu>
            </Box>
            <IconButton
                id="menu-button"
                size="small"
                onClick={e => setMenuAnchorEl(e.currentTarget)}
                sx={{
                    position: 'absolute',
                    right: 0,
                    top: 2,
                    padding: 0,
                    width: 20,
                    height: 20,
                }}
            >
                <MoreVertRounded fontSize="small" />
            </IconButton>
        </Box>
    );
}

export default CustomBlock;
